import { Link, useLocation } from 'react-router-dom'
import { Brand } from '@shared/components'
import { UserMenu } from './UserMenu'
import { AllowanceBadge } from './AllowanceBadge'

import { useEffect } from 'react'
import { adoptAccountUi } from '../uiPrefs'
import { useBackHandler } from '../backOverride'
import { setNavSlot } from '../pageName'

/**
 * The bar that sits above every signed-in page.
 *
 * Deliberately bare: the overview is the navigation, so section links and a
 * data-source badge would only repeat what that page already offers.
 *
 * Every page except the overview carries a way back to it. The logo has always
 * been that way back, but nothing said so — a skill opens full-screen and the
 * only clue is a mark in the corner that looks like decoration. A labelled
 * control costs one line and removes the question.
 */
export function TopNav() {
  const { pathname } = useLocation()
  const atHome = pathname === '/'
  /**
   * Back to the half of the overview this page belongs to. Coming out of a
   * project and landing on Skills is the page forgetting where you were — and
   * the projects are right there, one tab away, which makes it worse rather
   * than easier to forgive.
   */
  const back = pathname.startsWith('/projects') ? '/?tab=projects' : '/'
  /** A page with steps of its own may take Back over (backOverride.ts). */
  const stepBack = useBackHandler()
  // The page's name is no longer shown after WonderWorks (Huanrui, 2026-10-21):
  // the bar is the words alone.
  // Signed in by the time this bar is drawn: the skin the account remembers.
  useEffect(() => {
    adoptAccountUi()
  }, [])

  return (
    <header className="web-nav">
      {/* A link again, and this time it goes somewhere: the tab now lives in
          the address, so this lands on Skills rather than on whatever the
          overview last happened to be showing. */}
      <Link to="/" aria-label="WonderWorks — back to tools">
        <Brand compact split noMark />
      </Link>
      {/* A page's own control beside the name: the project's title (2026-10-21). */}
      <span className="web-nav__slot" ref={setNavSlot} />
      <span className="web-nav__spacer" />
      {!atHome && stepBack ? (
        <button type="button" className="web-back" onClick={stepBack}>
          <span aria-hidden="true">&#8592;</span>
          Back
        </button>
      ) : !atHome ? (
        <Link className="web-back" to={back}>
          <span aria-hidden="true">&#8592;</span>
          Back
        </Link>
      ) : null}
      {/* What is left of this month's allowance, where the sun-and-moon
          button was (2026-10-19: black only). */}
      <AllowanceBadge />
      <UserMenu />
    </header>
  )
}
